import { useContext, useEffect, useMemo, useRef } from 'react'

import { useTaskProgress } from '@/feature/execution-board/api/useTaskProgress'
import { MapMarker, MapShape } from '@/feature/map'
import { MapContext } from '@/feature/map/context/map'
import { calculateZoomForBounds } from '@/feature/map/utils/calculateZoomForBounds'
import { useTaskAssetPosition } from '@/feature/observatory/hooks/useTaskAssetPosition'
import { TASK_MARKER } from '@/feature/observatory/utils/marker-constants'

import {
  TaskCheckpointMarkerIcon,
  TaskEndMarkerIcon,
  TaskStartMarkerIcon,
} from '@/feature/observatory/components/markers/TaskMarker'
import { Tooltip } from '@/lib/core/components/Tooltip/Tooltip'

import { curvedPath } from './curvedPath'
import { getTaskMarkers } from './getTaskMarkers'
import styles from './TaskMapOverlay.module.css'
import { TaskMapOverlayProps } from './TaskMapOverlay.types'

// How long to wait for the assigned asset's first live position before fitting the view without
// it. Keeps the initial pan to a single move (stops + asset together) while not stalling the pan
// indefinitely when the asset isn't reporting.
const ASSET_POSITION_WAIT_MS = 2000

/**
 * Renders the fully exploded route for a focused task: every location marker plus the
 * connecting polyline. Mounted only while the task is focused, so on mount it fits the
 * whole route into the viewport.
 */
export const TaskMapOverlay = ({ task, onViewGeofence }: TaskMapOverlayProps) => {
  const map = useContext(MapContext)
  const markers = useMemo(() => getTaskMarkers(task), [task])

  // Progress data tells us which stops have been reached, so visited checkpoints render as solid
  // (black) pins and still-to-reach ones as hollow (white) pins. Same fetch the popout uses (SWR
  // dedupes). Until it loads, checkpoints stay solid so nothing flashes hollow.
  const { data: progress } = useTaskProgress(task.taskDefinition.id)
  const visitedLocationIds = useMemo(
    () => new Set(progress?.locations.visited.map((location) => location.id) ?? []),
    [progress],
  )
  const hasProgress = !!progress

  // Live position of the task's assigned asset. Held in a ref so the bounds fit below can read the
  // latest value without re-firing on every telemetry tick (which would keep yanking the map as
  // the vehicle moves). `hasAssetPosition` only flips when it first becomes available.
  const assetPosition = useTaskAssetPosition(task)
  const assetPositionRef = useRef(assetPosition)
  assetPositionRef.current = assetPosition
  const hasAssetPosition = assetPosition != null

  // The view is fitted exactly once per focused task, so the stops and the asset land in a single
  // pan/zoom rather than one pan for the stops and a second when the asset position arrives.
  const hasFittedRef = useRef(false)

  const points = useMemo(
    () => markers.map((marker) => ({ lat: marker.position.lat, lng: marker.position.lng })),
    [markers],
  )

  // One polyline per consecutive marker pair so each segment gets its own arrowhead.
  const segments = useMemo(
    () => points.slice(0, -1).map((from, i) => curvedPath([from, points[i + 1]])),
    [points],
  )

  // Reset the one-time fit when a different task is focused (the component is also remounted via a
  // key, but this keeps the behaviour correct if that ever changes).
  useEffect(() => {
    hasFittedRef.current = false
  }, [task.id])

  // Fit the route AND the assigned asset into view in a single pan when the task becomes focused.
  // If the task has an assigned vehicle we briefly wait for its first live position so both are
  // framed together, rather than panning to the stops and then re-panning once the asset arrives;
  // otherwise the fit happens on the next tick.
  //
  // The fit is always deferred (setTimeout), never run synchronously here. The map's own initial
  // "default position" pan lives in the parent map component, whose effect fires AFTER this child
  // effect in the same commit — a synchronous fit would be immediately clobbered by that default
  // pan, leaving the map over the default view instead of the focused task.
  useEffect(() => {
    if (hasFittedRef.current || !map?.map || markers.length === 0) return

    const fit = () => {
      if (hasFittedRef.current || !map?.map) return

      const bounds = new window.google.maps.LatLngBounds()
      markers.forEach((marker) => bounds.extend(marker.position))
      if (assetPositionRef.current) {
        bounds.extend(assetPositionRef.current)
      }

      const targetZoom = calculateZoomForBounds(bounds, map.map.getDiv().getBoundingClientRect())
      map.panTo({
        position: bounds.getCenter(),
        zoom: targetZoom - 0.1,
      })
      hasFittedRef.current = true
    }

    // Wait for the asset only when one is assigned but its position hasn't arrived yet.
    const waitForAsset = task.assignedVehicle != null && assetPositionRef.current == null
    const timeoutId = setTimeout(fit, waitForAsset ? ASSET_POSITION_WAIT_MS : 0)
    return () => clearTimeout(timeoutId)
  }, [map, markers, hasAssetPosition, task.assignedVehicle])

  const renderMarkers = useMemo(() => {
    return markers.map((marker, index) => {
      // Without an injected handler there is nowhere for the action to go, so the marker renders
      // bare rather than behind an inert tooltip.
      const isGeofenceLocation =
        marker.locationType === 'GEOFENCE' && marker.locationObjectId && onViewGeofence
      const isFirst = index === 0
      const isLast = index === markers.length - 1

      // Before progress loads, treat checkpoints as visited (solid) so they don't flash hollow.
      const isVisited = hasProgress ? visitedLocationIds.has(marker.locationId) : true

      const markerIcon = isFirst ? (
        <TaskStartMarkerIcon />
      ) : isLast ? (
        <TaskEndMarkerIcon />
      ) : (
        <TaskCheckpointMarkerIcon counter={index + 1} visited={isVisited} />
      )

      const markerButton = (
        <button type="button" className={styles.marker} data-testid="task-route-marker">
          {markerIcon}
        </button>
      )

      return (
        <MapMarker
          key={marker.id}
          position={{ lat: marker.position.lat, lng: marker.position.lng }}
          zIndex={TASK_MARKER}
          clusterer={false}
        >
          <div className={styles.markerContainer}>
            {isGeofenceLocation ? (
              <Tooltip
                placement="top"
                trigger="mouseenter"
                tooltipContent={
                  <div className={styles.tooltipContent}>
                    <button
                      type="button"
                      className={styles.tooltipButton}
                      onClick={() =>
                        onViewGeofence(marker.locationObjectId as number, marker.position)
                      }
                    >
                      View Geofence
                    </button>
                  </div>
                }
              >
                {markerButton}
              </Tooltip>
            ) : (
              markerButton
            )}
          </div>
        </MapMarker>
      )
    })
  }, [markers, onViewGeofence, hasProgress, visitedLocationIds])

  return (
    <>
      {renderMarkers}

      {segments.map((segmentPath, i) => {
        // A segment leads INTO markers[i + 1]. If that stop hasn't been reached yet we render the
        // upcoming leg as a grey dashed line (matching the hollow/unvisited pin theming); completed
        // legs between reached stops stay solid black. Before progress loads everything is solid.
        const destinationVisited = hasProgress
          ? visitedLocationIds.has(markers[i + 1].locationId)
          : true
        const color = destinationVisited ? '#000000' : '#9ca3af'

        const arrow = {
          icon: {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            fillColor: color,
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 3,
          },
          offset: '50%',
        }

        // For unvisited legs the stroke is drawn entirely by repeated dash symbols (the base line
        // is made invisible via strokeOpacity: 0) so the segment reads as dashed rather than solid.
        const dash = {
          icon: {
            path: 'M 0,-1 0,1',
            strokeColor: color,
            strokeOpacity: 1,
            strokeWeight: 2,
            scale: 3,
          },
          offset: '0',
          repeat: '12px',
        }

        return (
          <MapShape
            key={i}
            shape={window.google.maps.Polyline}
            parameters={{
              geodesic: false,
              path: segmentPath,
              strokeColor: color,
              strokeOpacity: destinationVisited ? 1 : 0,
              strokeWeight: 2,
              icons: destinationVisited ? [arrow] : [dash, arrow],
            }}
          />
        )
      })}
    </>
  )
}
